import React, { useMemo } from 'react'
import { ThemeProvider } from '@mui/material/styles'

import { useAppSelector } from 'redux/store'
import ThemeInterface from '@/theme-interface'
import { createMuiThemeWithColorMode } from '@/theme'
import { ColorModes } from '@/modules/layout/constants'
import { IUserModel } from '@/modules/account/model/UserModelType'
import { useEmailConfirmationBar } from '@/hooks/useEmailConfirmationPending'
import { EmailConfirmationBarHeight } from '@/themes/base'

type Props = {
  user: IUserModel
  children: React.ReactNode
}

const AppThemeProvider = (props: Props) => {
  const { user, children } = props
  const darkMode = useAppSelector(state => state.user.info.layoutSettings?.darkMode)
  const themeMode = darkMode ? ColorModes.dark : ColorModes.light

  const printMode = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    return searchParams.get('print')
  }, [location.search])

  const withEmailConfirmationBar = useEmailConfirmationBar()

  const theme = useMemo(() => {
    const muiTheme = createMuiThemeWithColorMode(themeMode) as ThemeInterface
    if (printMode || user?.isDesktopTenant) {
      muiTheme.pageContentOffset = 0
      muiTheme.menuHeight = 0
    } else if (withEmailConfirmationBar) {
      // DO-13065: the header grows by the bar; every `calc(100vh - ${theme.menuHeight}px)` page follows
      muiTheme.menuHeight += EmailConfirmationBarHeight
      muiTheme.pageContentOffset += EmailConfirmationBarHeight
    }
    return muiTheme
  }, [printMode, user?.isDesktopTenant, themeMode, withEmailConfirmationBar])

  return (
    <ThemeProvider theme={theme}>
      {children}
    </ThemeProvider>
  )
}

export default AppThemeProvider
